import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { api, gbp, gbp2 } from "@/lib/api";

/**
 * TopupLanding — public landing page opened from the top-up invite email.
 * Fetches the client / treatment / permitted dates and renders a locked
 * mini booking flow (treatment cannot be changed, only 5 dates selectable).
 */
export default function TopupLanding() {
  const { tid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [tp, setTp] = useState(null);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/topup/${tid}`, { params: { token } })
      .then((r) => setTp(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This top-up link is invalid or expired"));
  }, [tid, token]);

  useEffect(() => {
    if (!selectedDate || !tp) return;
    setLoadingSlots(true);
    setSelectedTime(null);
    api.get("/slots", { params: { date: selectedDate, duration: tp.treatment_duration || 30 } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => toast.error("Could not load times"))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, tp]);

  const submit = async () => {
    if (!selectedDate || !selectedTime) { toast.error("Please pick a date and time"); return; }
    setSubmitting(true);
    try {
      const body = {
        items: [{ treatment_id: tp.treatment_id, quantity: 1 }],
        date: selectedDate,
        time: selectedTime,
        client: {
          first_name: (tp.client_name || "").split(" ")[0] || "Client",
          last_name: (tp.client_name || "").split(" ").slice(1).join(" ") || "-",
          email: tp.client_email,
          phone: tp.client_phone || "",
          address: "-", postcode: "-",
          medical_consent: true, age_confirmation: true,
        },
        origin_url: window.location.origin,
        consents: [],
        topup_lock: { topup_id: tid, topup_token: token },
      };
      const r = await api.post("/bookings", body);
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Top-up booking confirmed!");
        nav(`/topup/confirmed?bid=${r.data.booking_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  if (error) return <><Header /><div className="max-w-2xl mx-auto p-8"><h1 className="font-serif text-3xl">Top-up unavailable</h1><p className="text-stone-600 mt-4">{error}</p></div></>;
  if (!tp) return <><Header /><div className="max-w-2xl mx-auto p-8"><Loader2 className="animate-spin" /></div></>;

  return (
    <>
      <Header />
      <div className="max-w-3xl mx-auto p-6 lg:p-10" data-testid="topup-landing">
        <p className="mono-label text-[#8A6D2A]">TOP-UP / REVIEW · {tp.status.toUpperCase()}</p>
        <h1 className="font-serif text-4xl mt-2">Hi {(tp.client_name || "there").split(" ")[0]},</h1>
        <p className="text-stone-600 mt-3 leading-relaxed">Book your <strong>{tp.treatment_name}</strong> top-up on any of the 5 dates below. This is your only permitted booking window — dates outside this range are not available for the top-up.</p>

        <div className="mt-8 border hairline bg-white p-6" data-testid="topup-treatment-locked">
          <p className="mono-label">Your Treatment (locked)</p>
          <div className="mt-2 flex items-end justify-between gap-4">
            <span className="font-serif text-2xl">{tp.treatment_name}</span>
            <span className="mono-label gold text-sm">COMPLIMENTARY</span>
          </div>
          <p className="text-xs text-stone-500 mt-2">Your top-up is included with your previous treatment. No payment required.</p>
        </div>

        <div className="mt-8">
          <p className="mono-label mb-3">Choose your top-up date</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {tp.available_booking_dates.map((d) => {
              const dt = new Date(d);
              return (
                <button
                  key={d}
                  data-testid={`topup-date-${d}`}
                  onClick={() => setSelectedDate(d)}
                  className={`p-4 border transition text-center ${selectedDate === d ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:border-stone-900"}`}
                >
                  <div className="mono-label text-[10px] opacity-70">{dt.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                  <div className="font-serif text-2xl mt-1">{dt.toLocaleDateString("en-GB", { day: "numeric" })}</div>
                  <div className="mono-label text-[10px] opacity-70">{dt.toLocaleDateString("en-GB", { month: "short" })}</div>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDate && (
          <div className="mt-8">
            <p className="mono-label mb-3">Available times</p>
            {loadingSlots ? <p className="text-sm text-stone-500">Loading…</p> :
              slots.length === 0 ? <p className="text-sm text-stone-500 py-8 text-center border hairline bg-white">No times available on this date — try another date.</p> :
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {slots.map((s) => (
                  <button
                    key={s}
                    data-testid={`topup-time-${s}`}
                    onClick={() => setSelectedTime(s)}
                    className={`p-3 border ${selectedTime === s ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:border-stone-900"}`}
                  >{s}</button>
                ))}
              </div>}
          </div>
        )}

        <div className="mt-10 border-t hairline pt-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label">Amount to pay today</p>
            <p className="font-serif text-3xl gold mt-1">£0.00</p>
            <p className="text-xs text-stone-500">Complimentary top-up</p>
          </div>
          <button
            data-testid="topup-submit"
            disabled={!selectedDate || !selectedTime || submitting || tp.status !== "Eligible"}
            onClick={submit}
            className="bg-stone-900 text-white px-8 py-4 mono-label hover:bg-[#C5A059] transition disabled:opacity-30"
            style={{ color: "#fff" }}
          >{submitting ? "Confirming…" : tp.status === "Eligible" ? "Confirm Top-Up Booking" : `Top-up is ${tp.status.toUpperCase()}`}</button>
        </div>
        {tp.status === "Upcoming" && <p className="text-xs text-amber-700 mt-3">Your top-up window opens on {tp.topup_start_date}. You can only book from that date onward.</p>}
        {tp.status === "Expired" && <p className="text-xs text-amber-700 mt-3">Your top-up window has closed. Please contact CLAÈ Aesthetics directly for options.</p>}
      </div>
    </>
  );
}
